import React from "react";
import { Avatar, Button, Space, Typography } from "antd";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import SectionCard from "../../../components/shared/SectionCard";
import InlineLoading from "../../../components/shared/InlineLoading";
import EmptyState from "../../../components/shared/EmptyState";
import { DashboardNewestPatient } from "../../../api/healthcare/DashboardApi";
import type { UserUuid } from "../../../types/identity";

dayjs.extend(relativeTime);

const { Text } = Typography;

interface NewestPatientsPanelProps {
	newestPatients: DashboardNewestPatient[];
	isLoading: boolean;
	onOpenPatient?: (id: UserUuid) => void;
}

const NewestPatientsPanel: React.FC<NewestPatientsPanelProps> = ({ newestPatients, isLoading, onOpenPatient }) => {
	const navigate = useNavigate();

	return (
		<SectionCard
			title={
				<Space size={6} align="center">
					<TeamOutlined />
					<span>Newest Patients</span>
				</Space>
			}
			extra={
				<Button size="small" type="link" onClick={() => navigate("/healthcare/patients")}>
					View all
				</Button>
			}
			style={{ height: "100%" }}
		>
			{isLoading ? (
				<InlineLoading minHeight={160} message="Loading patients…" />
			) : newestPatients.length === 0 ? (
				<EmptyState
					title="No patients yet"
					description="Newly registered patients will appear here"
					style={{ padding: "24px 0" }}
				/>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
					{newestPatients.map((patient, idx) => {
						const isLast = idx === newestPatients.length - 1;
						const fullName = `${patient.first_name} ${patient.last_name}`.trim();
						const initials = `${patient.first_name?.[0] ?? ""}${patient.last_name?.[0] ?? ""}`.toUpperCase();
						return (
							<div
								key={patient.id}
								onClick={() => onOpenPatient?.(patient.id)}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									padding: "8px 0",
									borderBottom: isLast ? "none" : "1px solid var(--mg-border)",
									cursor: "pointer",
								}}
							>
								<Avatar
									src={patient.profile_picture ?? undefined}
									icon={!patient.profile_picture ? <UserOutlined /> : undefined}
									size={32}
									style={{ flexShrink: 0, backgroundColor: "var(--mg-primary)" }}
								>
									{!patient.profile_picture ? initials : null}
								</Avatar>
								<div style={{ flex: 1, minWidth: 0 }}>
									<Text style={{ fontSize: 12, fontWeight: 600, display: "block" }} ellipsis>
										{fullName}
									</Text>
									<Text type="secondary" style={{ fontSize: 11 }}>
										{dayjs(patient.created_at).fromNow()}
									</Text>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</SectionCard>
	);
};

export default NewestPatientsPanel;
